import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteRecognition } from 'api/recognitions.service';
import { useToast } from 'hooks/ui';
import {
  resetDraft,
  setDraftRecognition,
  useRecognitionStore,
} from 'store/recognition';
import { recognitionRoutes } from 'router/recognitionRoutes';
import { useApplicationStore } from 'store/application';
import { Recognition } from 'models/recognition.model';
import { recognitionKeys } from './queryKeys';

export const useRecognitionDeleteQuery = () => {
  const navigate = useNavigate();
  const recognitions = useRecognitionStore((state) => state.recognitions);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore();

  const discardDraft = (): void => {
    resetDraft();

    if (recognitions.length) {
      setDraftRecognition(recognitions[0]);
      navigate(recognitionRoutes.recognitionId(recognitions[0]?.id));

      return;
    }

    navigate(recognitionRoutes.base);
  };

  const { isPending, isError, mutate } = useMutation({
    mutationFn: (id: string) => {
      setLoading();

      return deleteRecognition(id);
    },
    onSuccess: ({ id }: Recognition) => {
      queryClient.invalidateQueries({ queryKey: recognitionKeys.all });
      toast.success('Recognition has been deleted successfully');

      const nextRecognition = recognitions.find(
        (recognition: Recognition) => recognition.id !== id,
      );

      if (nextRecognition) {
        navigate(recognitionRoutes.recognitionId(nextRecognition.id));
        setDraftRecognition(nextRecognition);
      } else {
        navigate(recognitionRoutes.notFound);
        resetDraft();
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => removeLoading(),
  });

  return { isLoading: isPending, isError, mutate, discardDraft };
};
